import React from 'react';
import { toKebabCase } from 'helpers/common';

export type RadioGroupOption = {
    id: string;
    value: string | number;
    label: string;
    description?: string;
    hiddenLabel?: boolean;
    disabled?: boolean;
};

type RadioGroupProps = {
    name: string;
    options: RadioGroupOption[];
    selectedValue;
    readonly?: boolean;
    lineSeparated?: boolean;
    containerClass?: string;
    optionContainerClass?: string;
    onChange: (value: string) => void;
};

const RadioGroup = ({
    name,
    options,
    selectedValue,
    readonly,
    lineSeparated,
    containerClass = '',
    optionContainerClass = '',
    onChange
}: RadioGroupProps) => {
    return (
        <div className={'d-flex flex-column gap-2 ' + containerClass}>
            {options.map((option, i) => {
                const isLastOption = i === options.length - 1;

                const labelClass = option.hiddenLabel ? 'visually-hidden' : '';

                const separatorClass =
                    lineSeparated && !isLastOption ? 'border-bottom pb-2' : '';

                const optionClass =
                    `form-check ${optionContainerClass} ${separatorClass}`.trim();

                return (
                    <div
                        className={optionClass}
                        key={option.id}
                        data-testid={`radio-option-${toKebabCase(String(option.value))}`}>
                        <label
                            htmlFor={option.id}
                            className='d-flex align-items-center gap-3'
                            style={{ cursor: 'pointer' }}>
                            <input
                                type='radio'
                                value={option.value}
                                id={option.id}
                                name={name}
                                className='form-check-input'
                                disabled={readonly ?? option.disabled}
                                checked={selectedValue === option.value}
                                onChange={(e) =>
                                    !option.disabled && onChange(e.target.value)
                                }
                            />
                            <div className='d-flex flex-column gap-1'>
                                <div className={labelClass}>{option.label}</div>

                                {option.description && (
                                    <div className={'small text-muted'}>
                                        {option.description}
                                    </div>
                                )}
                            </div>
                        </label>
                    </div>
                );
            })}
        </div>
    );
};

export default RadioGroup;
